import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { companyApi, vendorActionBadgeCount } from '@l2u/web-shared';
import type { Question, Submission } from '@l2u/web-shared';

// Spelled out (30s — the same as the app-wide default in web-shared's
// queryClient.ts, which the wizard inherits) so a shell remount, which happens
// on every navigation between portal sections, reads the cache instead of
// refetching both lists even if that default ever changes.
export const VENDOR_ACTION_STALE_MS = 30_000;

/**
 * The two list queries behind the badge. The query keys AND fetchers are
 * exactly the ones QuestionnaireWizard uses, so the shell and the wizard share
 * one cache entry per list: no extra requests once the questionnaire has been
 * opened, and every mutation the wizard makes (it invalidates `meQueryKey`, a
 * prefix of the submissions key) refreshes the badge too. Keep the two in step
 * if either moves.
 */
export function vendorActionQueryOptions(apiPrefix: string, meQueryKey: readonly unknown[]) {
  return {
    questions: {
      queryKey: [`${apiPrefix}/questions`],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        companyApi.get<{ questions: Question[] }>(`${apiPrefix}/questions`, { signal }),
      staleTime: VENDOR_ACTION_STALE_MS,
    },
    submissions: {
      queryKey: [...meQueryKey, 'submissions'],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        companyApi.get<{ submissions: Submission[] }>(`${apiPrefix}/submissions`, { signal }),
      staleTime: VENDOR_ACTION_STALE_MS,
    },
  };
}

/**
 * The nav badge's "things you need to action" total for one portal (company:
 * `/api/me` + `['me']`; driver: `/api/driver/me` + `['driver-me']`).
 *
 * Returns null until BOTH lists are loaded (or if either fails), so the caller
 * can fall back to what it showed before rather than flash a partial count.
 *
 * WHEN THIS STILL RUNS. The API now computes the same number from the same
 * loaders and rule (GET /api/me/action-count, GET /api/driver/me/action-count;
 * @l2u/shared/vendorActions), and the shells show that via
 * useQuestionnaireBadgeCount below. This hook is enabled only when that
 * endpoint FAILS — an API deployed before it (a 404), or any other error — so
 * on a current API the shell fetches neither list. Removable once no API
 * without the endpoint can be serving.
 */
export function useVendorActionCount(
  apiPrefix: string,
  meQueryKey: readonly unknown[],
  enabled: boolean,
): number | null {
  const opts = vendorActionQueryOptions(apiPrefix, meQueryKey);
  const questionsQuery = useQuery({ ...opts.questions, enabled });
  const submissionsQuery = useQuery({ ...opts.submissions, enabled });
  const questions = questionsQuery.data?.questions;
  const submissions = submissionsQuery.data?.submissions;
  return useMemo(
    () => (questions && submissions ? vendorActionBadgeCount(questions, submissions) : null),
    [questions, submissions],
  );
}

/**
 * The server-counted Questionnaire badge. Its own key — NOT under ['me'] /
 * ['driver-me']: the wizard invalidates those prefixes after every step save,
 * and this count (~5 serial round trips server-side) moves only on a submit,
 * resubmit or reply. QuestionnaireWizard refetches it when the count from its
 * own lists changes (actionCountKey).
 */
export const COMPANY_ACTION_COUNT_KEY = ['company-action-count'] as const;
export const DRIVER_ACTION_COUNT_KEY = ['driver-action-count'] as const;

const BADGE_SOURCES = {
  company: { key: COMPANY_ACTION_COUNT_KEY, apiPrefix: '/api/me', meQueryKey: ['me'] },
  driver: {
    key: DRIVER_ACTION_COUNT_KEY,
    apiPrefix: '/api/driver/me',
    meQueryKey: ['driver-me'],
  },
} as const;

/**
 * The shell's Questionnaire badge: GET <prefix>/action-count, fetched in the
 * background after mount (nothing waits on it), falling back to counting the
 * two full lists only when that request fails. `count` is null until known;
 * `error` is the endpoint's own failure, for the shell's redirect gate.
 */
export function useQuestionnaireBadgeCount(
  kind: 'company' | 'driver',
  enabled: boolean,
): { count: number | null; error: unknown } {
  const source = BADGE_SOURCES[kind];
  const server = useQuery({
    queryKey: source.key,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      companyApi.get<{ count: number }>(`${source.apiPrefix}/action-count`, { signal }),
    staleTime: VENDOR_ACTION_STALE_MS,
    enabled,
  });
  const fallback = enabled && server.isError;
  const listCount = useVendorActionCount(source.apiPrefix, source.meQueryKey, fallback);
  const count = server.data?.count ?? (fallback ? listCount : null);
  return { count: typeof count === 'number' ? count : null, error: server.error };
}
